import { Tooltip } from "@fluentui/react-components";
import { AddRegular, DeleteRegular, EditRegular } from "@fluentui/react-icons";
import { adminDeleteLink, adminGetLinks } from "apps/project-iq/api";
import {
  DIAGNOSTIC_RUN_CATEGORY,
  IAdminLink,
  LINK_CATEGORY_LABELS,
} from "apps/project-iq/models/IAdminLink";
import { DiagnosticMethod } from "apps/project-iq/models/IDiagnostic";
import { METHOD_LABELS } from "apps/project-iq/utils/diagnosticUtils";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import {
  IAdminListAction,
  IAdminRowAction,
} from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { ITableColumn } from "models/ITableColumn";
import { useEffect, useMemo, useState } from "react";
import { EditLinkDialog } from "./EditLinkDialog";
import { useManageIqLinksStyles } from "./ManageIqLinks.styles";
import { IDialogState } from "./ManageIqLinks.types";

const LINKS_PER_PAGE = 15;
const LINKS_ACTIONS_COLUMN_WIDTH = "115px";

export const ManageIqLinks = (): JSX.Element => {
  const colStyles = useAdminColumnStyles();
  const linkStyles = useManageIqLinksStyles();
  const [links, setLinks] = useState<IAdminLink[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<IDialogState>({
    open: false,
    link: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<IAdminLink | null>(null);

  const loadLinks = async (silent = false): Promise<void> => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await adminGetLinks();
      setLinks(data as IAdminLink[]);
    } catch {
      setError("Failed to load links. Please try again.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void loadLinks();
  }, []);

  const handleSave = (): void => {
    setDialogState({ open: false, link: null });
    void loadLinks();
  };

  const handleDismiss = (): void => {
    setDialogState({ open: false, link: null });
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await adminDeleteLink(deleteTarget.id);
      setDeleteTarget(null);
      void loadLinks();
    } catch {
      setError("Failed to delete link. Please try again.");
      setDeleteTarget(null);
    }
  };

  const columns: ITableColumn<IAdminLink>[] = useMemo(
    () => [
      {
        columnId: "category",
        displayValue: "Category",
        className: colStyles.normal,
        renderHeaderCell: () => "Category",
        compare: (a: IAdminLink, b: IAdminLink) =>
          LINK_CATEGORY_LABELS[a.category].localeCompare(
            LINK_CATEGORY_LABELS[b.category],
          ),
        renderCell: (item: IAdminLink) => (
          <span>{LINK_CATEGORY_LABELS[item.category]}</span>
        ),
      },
      {
        columnId: "linkType",
        displayValue: "Link Type",
        className: colStyles.normal,
        renderHeaderCell: () => "Link Type",
        compare: (a: IAdminLink, b: IAdminLink) => {
          const labelA = a.linkType ? METHOD_LABELS[a.linkType] : "";
          const labelB = b.linkType ? METHOD_LABELS[b.linkType] : "";
          return labelA.localeCompare(labelB);
        },
        renderCell: (item: IAdminLink) => (
          <span>{item.linkType ? METHOD_LABELS[item.linkType] : "—"}</span>
        ),
      },
      {
        columnId: "displayName",
        displayValue: "Link Display Name",
        className: colStyles.normal,
        renderHeaderCell: () => "Link Display Name",
        compare: (a: IAdminLink, b: IAdminLink) =>
          a.displayName.localeCompare(b.displayName),
        renderCell: (item: IAdminLink) => (
          <span className={linkStyles.labelCell}>{item.displayName}</span>
        ),
      },
      {
        columnId: "url",
        displayValue: "Link URL",
        className: colStyles.wide,
        renderHeaderCell: () => "Link URL",
        compare: (a: IAdminLink, b: IAdminLink) => a.url.localeCompare(b.url),
        renderCell: (item: IAdminLink) => (
          <Tooltip
            content={{
              children: item.url,
              style: { maxWidth: "400px", wordBreak: "break-all" },
            }}
            relationship="description"
            withArrow
          >
            <span className={linkStyles.urlCell}>{item.url}</span>
          </Tooltip>
        ),
      },
    ],
    [colStyles, linkStyles],
  );

  const existingDiagnosticLinkTypes: DiagnosticMethod[] = useMemo(
    () =>
      links
        .filter(
          (link) =>
            link.category === DIAGNOSTIC_RUN_CATEGORY && link.linkType !== null,
        )
        .map((link) => link.linkType as DiagnosticMethod),
    [links],
  );

  const rowActions: IAdminRowAction<IAdminLink>[] = useMemo(
    () => [
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item: IAdminLink) =>
          setDialogState({ open: true, link: item }),
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (item: IAdminLink) => setDeleteTarget(item),
      },
    ],
    [],
  );

  const headerActions: IAdminListAction[] = useMemo(
    () => [
      {
        label: "Add Link",
        icon: <AddRegular />,
        appearance: "primary",
        disabled: loading,
        onClick: () => setDialogState({ open: true, link: null }),
      },
    ],
    [loading],
  );

  return (
    <>
      {error && (
        <div role="alert" className={linkStyles.errorAlert}>
          {error}
        </div>
      )}
      <AdminListPage<IAdminLink>
        title="Links"
        items={links}
        columns={columns}
        isLoading={loading}
        headerActions={headerActions}
        searchPlaceholder="Search links..."
        searchQuery=""
        onSearchChange={() => {}}
        rowActions={rowActions}
        getRowId={(item) => item.id}
        paginatedItems={links}
        selectedPageIndex={0}
        pageCount={1}
        totalItemsCount={links.length}
        itemsPerPage={LINKS_PER_PAGE}
        onPageChange={() => {}}
        emptyStateMessage="No links found."
        actionsWidth={LINKS_ACTIONS_COLUMN_WIDTH}
      />
      <EditLinkDialog
        open={dialogState.open}
        link={dialogState.link}
        existingDiagnosticLinkTypes={existingDiagnosticLinkTypes}
        onSave={handleSave}
        onDismiss={handleDismiss}
      />
      <ActionDialog
        isVisible={deleteTarget !== null}
        title="Delete Link"
        message={`Delete "${deleteTarget?.displayName}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => setDeleteTarget(null)}
      />
    </>
  );
};
